/**
 * COMPTABILITÉ — LA BILLETTERIE DES ASSOCIATIONS (PRETIX) D'UNE NUIT (16/10/2026).
 *
 * Les soirées des BDE vendent leurs billets sur Pretix : 979 le 09/09/2026,
 * 249 le 15/10 à côté des 424 de la soirée Shotgun. Ce panneau, posé sur la
 * fiche de nuit et sur la synthèse de la clôture, dit ce que la nuit en
 * retient — la conséquence d'abord :
 *
 *   · les billets VENDUS, qui comptent dans les entrées (la règle de la DAF),
 *     et les scannés posés à côté, comme un contrôle ;
 *   · ce qui entre au chiffre de la nuit quand le réglage « Billetterie
 *     associations dans le CA » est allumé : la valeur faciale moins la part
 *     de chaque association, événement par événement ;
 *   · éteint, la valeur faciale reste un REPÈRE : rien n'est additionné.
 *
 * Tous les chiffres viennent du serveur (`billetterie-associations.ts`) ;
 * l'écran ne recalcule rien. Un montant caché par les droits se dit
 * « réservé », jamais 0.
 */

import { Ticket } from 'lucide-react'
import { Link } from '../router.js'
import { formatMoney } from './money.js'
import { nombre } from './entrees.js'
import { dansLeChiffre, introduction, pastille, sousRetenu, type SituationNuitAssociations } from './billetterie-associations-vue.js'
import './finance-associations.css'

/** Un événement d'association de la nuit, tel que le serveur le sert. */
export interface EvenementAssociationVue {
  event_id: string
  name: string
  billetterie: string
  sold: number
  scanned: number
  unpriced: number
  orders: number
  face_cents: number | null
  share_per_ticket_cents: number | null
  share_origin: 'EVENEMENT' | 'DEFAUT' | string
  share_reason: string | null
  share_cents: number | null
  kept_cents: number | null
  fees_cents: number | null
  /**
   * L'association qui organise (20/10/2026) — servie seulement à qui détient
   * AUSSI « Consulter les associations » ; absente sinon.
   */
  association?: { id: string; name: string } | null
}

/** `associations` de la fiche de nuit et de la clôture. */
export interface AssociationsVue {
  entries_enabled: boolean
  revenue_enabled: boolean
  sold: number
  scanned: number
  face_cents: number | null
  share_cents: number | null
  kept_cents: number | null
  fees_cents: number | null
  /** Ce qui entre au chiffre de la nuit ; `null` = réglage éteint, ou réservé (`masked`). */
  revenue_cents: number | null
  default_share_cents: number | null
  events: EvenementAssociationVue[]
  read_through: string | null
  settled: boolean
  notes: string[]
  masked: boolean
  /** Clôture seulement : figée par la finalisation. */
  frozen?: boolean
  frozen_at?: string | null
}

const montant = (c: number | null, masque: boolean): string => (c === null ? (masque ? 'réservé' : '—') : formatMoney(c))

/** « 2,00 € par billet (défaut) » / « 0,00 € par billet (réglée pour l’événement) ». */
function partParBillet(e: EvenementAssociationVue, masque: boolean): string {
  if (e.share_per_ticket_cents === null) return masque ? 'réservée' : '—'
  return `${formatMoney(e.share_per_ticket_cents)} par billet${e.share_origin === 'EVENEMENT' ? ', réglée pour l’événement' : ', par défaut'}`
}

export type { SituationNuitAssociations }

export function BilletterieAssociations(props: {
  a: AssociationsVue | null | undefined
  /** `finance.reference.read` : le lien vers Comptabilité › Règles. */
  peutRegler?: boolean
  /** `soiree.read` : le lien vers Réglages Soirées › Billetteries (sans lui, la page refuse). */
  peutVoirSoirees?: boolean
  situation?: SituationNuitAssociations
}) {
  const a = props.a
  if (!a) return null
  const situation = props.situation ?? null
  // « On masque ce qui n'existe pas » — SAUF quand rien n'a été lu : une nuit
  // dont Pretix n'a pas été relu le dit, sans tableau (revue du 16/10).
  if (a.events.length === 0) {
    if (a.notes.length === 0) return null
    return (
      <section className="lc-panel fba" aria-labelledby="fba-titre">
        <div className="lc-panel__head fba__head">
          <span className="fih-head__icon" aria-hidden="true"><Ticket size={17} /></span>
          <h2 className="lc-panel__title" id="fba-titre">Billetterie des associations (Pretix)</h2>
          <span className="lc-panel__spacer" />
          <span className="lc-pill lc-pill--warning">Lecture Pretix incomplète</span>
        </div>
        <div className="lc-panel__body">
          <ul className="fba__notes fba__notes--seules">
            {a.notes.map((n) => <li key={n} className="lc-hint">{n}</li>)}
          </ul>
        </div>
      </section>
    )
  }
  const p = pastille(a, situation)
  const m = (c: number | null) => montant(c, a.masked)
  const auChiffre = dansLeChiffre(a, situation)
  return (
    <section className="lc-panel fba" aria-labelledby="fba-titre">
      <div className="lc-panel__head fba__head">
        <span className="fih-head__icon" aria-hidden="true"><Ticket size={17} /></span>
        <h2 className="lc-panel__title" id="fba-titre">Billetterie des associations (Pretix)</h2>
        <span className="lc-panel__spacer" />
        <span className={`lc-pill ${p.ton}`}>{p.texte}</span>
      </div>
      <div className="lc-panel__body">
        <p className="lc-hint fba__intro">
          {introduction(a, situation)}
          {' '}
          {a.entries_enabled
            ? 'Les billets vendus comptent dans les entrées, les scannés sont un contrôle.'
            : 'Les billets vendus ne comptent pas dans les entrées (Comptabilité › Règles).'}
        </p>

        <dl className="fba__chiffres">
          <div className="fba__chiffre">
            <dt>Billets vendus</dt>
            <dd>{nombre(a.sold)}</dd>
            <span className="fba__sous">dont {nombre(a.scanned)} scanné{a.scanned > 1 ? 's' : ''}</span>
          </div>
          <div className="fba__chiffre">
            <dt>Valeur faciale</dt>
            <dd>{m(a.face_cents)}</dd>
            <span className="fba__sous">Repère, payé par les acheteurs</span>
          </div>
          <div className="fba__chiffre">
            <dt>Parts des associations</dt>
            <dd>{m(a.share_cents)}</dd>
            <span className="fba__sous">
              {a.default_share_cents !== null ? `Par défaut ${formatMoney(a.default_share_cents)} par billet` : 'Événement par événement'}
            </span>
          </div>
          {a.fees_cents !== null ? (
            <div className="fba__chiffre">
              <dt>Frais Stripe estimés</dt>
              <dd>{m(a.fees_cents)}</dd>
              <span className="fba__sous">Pretix ne connaît pas les frais réels</span>
            </div>
          ) : null}
          <div className={`fba__chiffre${auChiffre ? ' fba__chiffre--retenu' : ''}`}>
            <dt>{auChiffre ? 'Au chiffre de la nuit' : 'Reste après les parts'}</dt>
            <dd>{auChiffre ? m(a.revenue_cents) : m(a.kept_cents)}</dd>
            <span className="fba__sous">{sousRetenu(a, situation)}</span>
          </div>
        </dl>

        <div className="fi-tablewrap fba__tablewrap">
          <table className="fi-table fba__table">
            <thead>
              <tr>
                <th scope="col">Événement</th>
                <th scope="col" className="fi-money">Vendus</th>
                <th scope="col" className="fi-money">Scannés</th>
                <th scope="col" className="fi-money">Valeur faciale</th>
                <th scope="col">Part de l’association</th>
                <th scope="col" className="fi-money">Reste</th>
              </tr>
            </thead>
            <tbody>
              {a.events.map((e) => (
                <tr key={e.event_id}>
                  <th scope="row">
                    <span className="fba__nom">{e.name}</span>
                    <span className="fba__sous">{e.billetterie}{e.unpriced > 0 ? ` · ${e.unpriced} sans prix` : ''}
                      {e.association ? <> · <Link href={`/exploitation/associations/${e.association.id}`}>{e.association.name}</Link></> : null}</span>
                  </th>
                  <td className="fi-money">{nombre(e.sold)}</td>
                  <td className="fi-money">{nombre(e.scanned)}</td>
                  <td className="fi-money">{m(e.face_cents)}</td>
                  <td>
                    <span>{partParBillet(e, a.masked)}</span>
                    {e.share_reason ? <span className="fba__sous">« {e.share_reason} »</span> : null}
                  </td>
                  <td className="fi-money">{m(e.kept_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {a.notes.length > 0 ? (
          <ul className="fba__notes">
            {a.notes.map((n) => <li key={n} className="lc-hint">{n}</li>)}
          </ul>
        ) : null}

        {/* Les liens suivent les droits : la part se règle d'abord dans les
            Règles de la Comptabilité (ses droits à elle) ; l'écran des
            Soirées n'est proposé qu'à qui peut l'ouvrir (revue du 16/10 :
            le rôle compta y était refusé). */}
        {props.peutRegler !== false || props.peutVoirSoirees ? (
          <p className="lc-hint fba__liens">
            La part de chaque événement se règle
            {props.peutRegler !== false ? <> dans <Link href="/comptabilite/regles">Comptabilité › Règles</Link>, avec le défaut et l’entrée au chiffre</> : null}
            {props.peutRegler !== false && props.peutVoirSoirees ? ', ou' : null}
            {props.peutVoirSoirees ? <> à côté de sa nuit dans <Link href="/settings/soirees/billetteries">Réglages Soirées › Billetteries</Link></> : null}.
            {a.frozen ? ' Cette clôture est finalisée : une part changée ensuite ne la modifie pas.' : ''}
          </p>
        ) : a.frozen ? <p className="lc-hint fba__liens">Cette clôture est finalisée : une part changée ensuite ne la modifie pas.</p> : null}
      </div>
    </section>
  )
}
